import React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel,
} from "@tanstack/react-table";
import { renderDefaultSelected } from "../../shared/Table/table.utils";
import {
  capitalizeAll,
  capitalizeFirstLetter,
  createAlphabet,
  formatDate,
} from "../../utils";
import classNames from "classnames";

function Table({ columns, data }) {
  const [expanded, setExpanded] = React.useState({});

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded: renderDefaultSelected(data, "all") || expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const tableClasses = ""; //classNames("w-full");
  const theadClasses = classNames("bg-gray-100 font-times-new-roman");
  const trClasses = ""; //classNames("w-full");
  const thClasses = classNames("bg-gray-100 py-2 text-left px-1");
  const tdClasses = classNames("py-2 px-1 bg-gray-100");

  return (
    <table id="invoice-table" className={tableClasses}>
      <thead className={theadClasses}>
        {table.getHeaderGroups().map((headerGroup, i) => (
          <tr className={trClasses} key={i}>
            {headerGroup.headers.map((header) => {
              if (header.id.includes("legs")) return null;
              return (
                <th key={header.id + 1000} className={thClasses}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody className="w-full">
        {table.getRowModel().rows.map((row, i) => {
          const route = row.original.route;
          const rowId = route && Number(row.id.split(".")[1]);
          const canExpand = row.getCanExpand();

          return (
            <>
              {canExpand && (
                <tr key={row.id}>
                  <td colSpan={11}>
                    DATE: {formatDate(row.original.transactionDate, null, true)}
                    <br />
                    TRUCK LOAD: {row.original.loadNumber}
                  </td>
                </tr>
              )}
              {!canExpand && (
                <tr key={row.id + 100}>
                  {row.getVisibleCells().map((cell) => {
                    if (cell.id.includes("legs")) return null;
                    return (
                      <td className={tdClasses} key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              )}
              {!canExpand && (
                <Route data={row.original} rowId={rowId} key={row.id + 200} />
              )}
            </>
          );
        })}
      </tbody>
    </table>
  );
}

const InvoiceTable = (props) => {
  const tableData =
    props.tableData &&
    props.tableData.map((item) => {
      return {
        ...item,
        subRows: item.legs,
      };
    });
  const columns = React.useMemo(() => props.columns, []);
  const data = React.useMemo(() => tableData, [tableData]);

  return props.tableData && <Table columns={columns} data={data} />;
};

export default InvoiceTable;

/**
 * Stops shown under an invoice PDF leg.
 * Regular handoffs keep the combined route: first origin and last destination.
 * Backhaul handoffs show the second leg's origin and destination.
 * @param {object} leg
 * @returns {{ origin: object|undefined, destination: object|undefined }}
 */
export function getInvoicePdfStops(leg) {
  const isBackhaul =
    String(leg?.loadType || "").trim().toLowerCase() === "backhaul";
  const secondLegRoute = leg?.handoffRoute;

  if (leg?.isHandoff && isBackhaul && secondLegRoute?.origin) {
    return {
      origin: secondLegRoute.origin,
      destination: secondLegRoute.destination,
    };
  }

  return {
    origin: leg?.route?.origin,
    destination: leg?.route?.destination,
  };
}

function renderAddress(data, id, point) {
  if (!data.route[id]) return;
  const { address, name } = data.route[id];
  const capitalName = capitalizeAll(name);
  const header = `${capitalizeFirstLetter(id)} (POINT ${point}):`;
  const street = capitalizeAll(address.address);
  const addressRest = `${capitalizeAll(address.city)}, ${capitalizeAll(
    address.state
  )} ${address.zipcode}`;

  return (
    <td className="text-left" colSpan={6.5}>
      {header}
      <br />
      {capitalName}
      <br />
      {street}
      <br />
      {addressRest}
    </td>
  );
}

function Route({ data, rowId }) {
  const alphabet = createAlphabet();
  const originPoint = alphabet[rowId];
  const destPoint = alphabet[rowId + 1];
  const selectionType = String(data?.mileageSelectionType || "")
    .toLowerCase()
    .trim();
  const isDefaultSelection = selectionType === "defaultmileage";
  const isCustomSelection = [
    "billedmileage",
    "billedmiles",
    "custominput",
    "currentmileage",
  ].includes(selectionType);

  // Backward-compat: treat mileageUsed=0 as unset for legacy legs.
  const snapshotMileage =
    Number.isFinite(Number(data?.mileageUsed)) && Number(data?.mileageUsed) > 0
      ? Number(data.mileageUsed)
      : null;
  const routeDefaultMileage =
    Number.isFinite(Number(data?.route?.defaultMileage)) &&
    Number(data?.route?.defaultMileage) >= 0
      ? Number(data.route.defaultMileage)
      : Number.isFinite(Number(data?.route?.mileage)) &&
          Number(data?.route?.mileage) >= 0
        ? Number(data.route.mileage)
        : null;
  const routeLegacyMileage =
    Number.isFinite(Number(data?.route?.mileage)) &&
    Number(data?.route?.mileage) >= 0
      ? Number(data.route.mileage)
      : Number.isFinite(Number(data?.route?.defaultMileage)) &&
          Number(data?.route?.defaultMileage) >= 0
        ? Number(data.route.defaultMileage)
        : null;
  const routeCustomMileage =
    Number.isFinite(Number(data?.route?.customMileage)) &&
    Number(data?.route?.customMileage) >= 0
      ? Number(data.route.customMileage)
      : Number.isFinite(Number(data?.route?.billedMiles)) &&
          Number(data?.route?.billedMiles) >= 0
        ? Number(data.route.billedMiles)
        : null;

  let displayMileage = routeDefaultMileage;
  if (isCustomSelection) {
    displayMileage = snapshotMileage ?? routeCustomMileage ?? routeDefaultMileage;
  } else if (isDefaultSelection) {
    displayMileage = routeDefaultMileage;
  } else {
    // Legacy legs with no mileageSelectionType:
    // if snapshot exists and differs from legacy baseline, treat as legacy custom.
    // Option C: legacy baseline prefers route.mileage before route.defaultMileage.
    const hasLegacyCustomSnapshot =
      snapshotMileage != null &&
      routeLegacyMileage != null &&
      Math.abs(snapshotMileage - routeLegacyMileage) > 0.000001;
    displayMileage = hasLegacyCustomSnapshot
      ? snapshotMileage
      : snapshotMileage ?? routeLegacyMileage;
  }
  const displayMileageText =
    displayMileage != null ? Number(displayMileage).toLocaleString("en-US") : "0";

  const stops = getInvoicePdfStops(data);
  const addressData = {
    ...data,
    route: {
      ...data.route,
      origin: stops.origin,
      destination: stops.destination,
    },
  };

  return !data.route ? (
    <tr>
      {/* <td colSpan={13}>
        {" "}
        *CPM FUEL SURCHARGE DETERMINED BY DISTANCE FROM POINT {originPoint} TO
        POINT {destPoint} ({data.route?.mileage?.toLocaleString("en-US")} MILES)
      </td> */}
    </tr>
  ) : (
    <>
      <tr>
        {renderAddress(addressData, "origin", originPoint)}
        {renderAddress(addressData, "destination", destPoint)}
      </tr>
      <tr>
        <td colSpan={13}>
          *CPM FUEL SURCHARGE DETERMINED BY DISTANCE FROM POINT {originPoint} TO
          POINT {destPoint} ({displayMileageText} MILES)
        </td>
      </tr>
    </>
  );
}
